import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';

import * as DividendsActions from './dividends.actions';
import { DividendsService } from '../../services/dividends.service';
import { selectSelectedStock } from '../stocks/stocks.selectors';
import { Store } from '@ngrx/store';

@Injectable({
  providedIn: 'root',
})
export class DividendsEffects {
  loadDividends$: any = {};
  loadDividend$: any = {};
  createDividend$: any = {};
  updateDividend$: any = {};
  deleteDividend$: any = {};
  dividendSuccess$: any = {};

  constructor(
    private actions$: Actions,
    private dividendsService: DividendsService,
    private router: Router,
    private store: Store
  ) {
    this.loadDividends$ = createEffect(() =>
      this.actions$.pipe(
        ofType(DividendsActions.loadDividends),
        switchMap(({ stockId, filters }) =>
          this.dividendsService.getDividends(stockId, filters?.pageNumber, filters?.pageSize).pipe(
            map((response) => {
              const data = response.data || response;
              const dividends = data.items || data;
              const pagination = {
                currentPage: data.pageNumber || 1,
                pageSize: data.pageSize || 20,
                totalItems: data.totalCount || 0,
              };
              return DividendsActions.loadDividendsSuccess({
                dividends,
                pagination,
              });
            }),
            catchError((error) =>
              of(DividendsActions.loadDividendsFailure({ error: error.message }))
            )
          )
        )
      )
    );

    this.loadDividend$ = createEffect(() =>
      this.actions$.pipe(
        ofType(DividendsActions.loadDividend),
        switchMap(({ id }) =>
          this.dividendsService.getDividendById(id).pipe(
            map((response) => {
              const dividend = response.data || response;
              return DividendsActions.loadDividendSuccess({ dividend });
            }),
            catchError((error) =>
              of(DividendsActions.loadDividendFailure({ error: error.message }))
            )
          )
        )
      )
    );

    this.createDividend$ = createEffect(() =>
      this.actions$.pipe(
        ofType(DividendsActions.createDividend),
        switchMap(({ dividend }) =>
          this.dividendsService.createDividend(dividend).pipe(
            map((response) => {
              const createdDividend = response.data || response;
              return DividendsActions.createDividendSuccess({ dividend: createdDividend });
            }),
            catchError((error) =>
              of(DividendsActions.createDividendFailure({ error: error.message }))
            )
          )
        )
      )
    );

    this.updateDividend$ = createEffect(() =>
      this.actions$.pipe(
        ofType(DividendsActions.updateDividend),
        switchMap(({ id, dividend }) =>
          this.dividendsService.updateDividend(id, dividend).pipe(
            map((response) => {
              const updatedDividend = response.data || response;
              return DividendsActions.updateDividendSuccess({ dividend: updatedDividend });
            }),
            catchError((error) =>
              of(DividendsActions.updateDividendFailure({ error: error.message }))
            )
          )
        )
      )
    );

    this.deleteDividend$ = createEffect(() =>
      this.actions$.pipe(
        ofType(DividendsActions.deleteDividend),
        switchMap(({ id }) =>
          this.dividendsService.deleteDividend(id).pipe(
            map(() => DividendsActions.deleteDividendSuccess({ id })),
            catchError((error) =>
              of(DividendsActions.deleteDividendFailure({ error: error.message }))
            )
          )
        )
      )
    );

    // Navigate after success
    this.dividendSuccess$ = createEffect(
      () =>
        this.actions$.pipe(
          ofType(
            DividendsActions.createDividendSuccess,
            DividendsActions.updateDividendSuccess,
            DividendsActions.deleteDividendSuccess
          ),
          tap(() => {
            this.store.select(selectSelectedStock).subscribe((stock) => {
              this.router.navigate(['/stocks', 'view', stock?.id, 'dividend']);
            });
          })
        ),
      { dispatch: false }
    );
  }
}
